import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios, { API } from "@/lib/api";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { useLang, formatRs } from "@/lib/i18n";
import { Brand, LangToggle } from "@/components/site/Header";
import { StatusBadge } from "@/components/StatusBadge";
import JuicePayment from "@/components/site/JuicePayment";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-6 py-3 border-b border-white/10 text-sm">
    <span className="text-slate-400">{label}</span><span className="text-right" data-testid={testid}>{value}</span>
  </div>
);

const Summary = ({ b, t, lang }) => (
  <div className="bg-[#0A1325] border border-white/10 p-6 sm:p-10">
    <Row label={t.f_excursion} value={t.exc_names[b.excursion]} testid="track-excursion" />
    <Row label={t.f_date} value={new Date(`${b.date}T00:00:00`).toLocaleDateString(lang, { dateStyle: "full" })} testid="track-date" />
    <Row label={t.f_guests} value={b.guests} />
    {b.options.length > 0 && <Row label={t.f_options} value={b.options.map((o) => t.options[o]).join(", ")} />}
    {b.price_total && <>
      <Row label={t.tr_total} value={formatRs(b.price_total)} testid="track-total" />
      <Row label={`${t.tr_deposit} (${b.deposit_pct}%)`} value={formatRs(b.deposit_amount)} testid="track-deposit" />
      <Row label={t.tr_balance} value={formatRs(b.price_total - b.deposit_amount)} />
    </>}
    {b.admin_note && <p className="mt-6 text-sm italic text-slate-300"><span className="text-[#D4AF37] not-italic">{t.tr_note} : </span>{b.admin_note}</p>}
  </div>
);

const StatusMessage = ({ b, t, lang }) => {
  const msg = {
    pending: `${t.tr_pending} ${new Date(b.respond_before).toLocaleString(lang, { dateStyle: "medium", timeStyle: "short" })}.`,
    payment_submitted: t.pay_submitted, confirmed: t.tr_confirmed, declined: t.tr_declined, cancelled: t.tr_cancelled,
  }[b.status];
  return msg ? <p className="text-slate-200 text-lg mb-10 max-w-2xl" data-testid="track-status-message">{msg}</p> : null;
};

export default function Track() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => axios.get(`${API}/bookings/track/${token}`).then((r) => setB(r.data)).catch(() => setError(true)), [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen" data-testid="track-page">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-10 h-20 flex items-center justify-between"><Brand /><LangToggle /></div>
      </header>
      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-16">
        {error && <p data-testid="track-not-found">{t.tr_notfound}</p>}
        {!b && !error && <p className="text-slate-400">{t.tr_loading}</p>}
        {b && <>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">JR-{b.token.slice(0, 6).toUpperCase()}</p>
          <h1 className="font-display text-5xl sm:text-6xl mb-6">{t.tr_title}</h1>
          <div className="mb-8"><StatusBadge status={b.status} label={t.status[b.status]} /></div>
          <StatusMessage b={b} t={t} lang={lang} />
          <Summary b={b} t={t} lang={lang} />
          {b.status === "approved" && <JuicePayment booking={b} onDone={load} />}
        </>}
        <Link to="/" className="inline-block mt-12 text-sm text-slate-400 hover:text-[#D4AF37] transition-colors duration-300" data-testid="back-home-link">← {t.back_home}</Link>
      </main>
      <WhatsAppFloat />
    </div>
  );
}
